import {v4 as uuidv4} from 'uuid';
import {useCallback} from "react";
import PropTypes from 'prop-types';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';

import {LoadingButton} from "@mui/lab";
import {styled} from "@mui/material/styles";
import {
  Box,
  Card,
  Grid,
  Stack,
  Button,
  Divider,
  Container,
  Typography
} from '@mui/material';

import {useTranslate} from 'src/locales';
import {putFile} from "src/services/file/put-api";
import {getSignedUrl} from "src/services/file/get-signed-url-api";

import Iconify from "src/components/iconify";
import FofooEditor from "src/components/fofoo-editor";
// components
import {
  RHFUpload, RHFTextField
} from 'src/components/hook-form';

const LabelStyle = styled(Typography)(({theme}) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

// ----------------------------------------------------------------------

PreparationForm.propTypes = {
  isEdit: PropTypes.bool,
  currentRecipe: PropTypes.object,
  onSubmit: PropTypes.func
};

export default function PreparationForm({isEdit, currentRecipe, onSubmit}) {
  const {
    control,
    setValue,
    formState: {isSubmitting},
    watch
  } = useFormContext();

  const {t} = useTranslate();

  const {fields: stepGroups, append: appendGroup, remove: removeGroup} = useFieldArray({
    control,
    name: 'stepGroups',
  });

  const {fields: steps, append: appendStep, remove: removeStep} = useFieldArray({
    control,
    name: 'steps',
  });

  const stepsWatched = watch("steps");

  const handleAddGroup = (key) => {
    const addedStepKey = uuidv4();
    appendGroup({
      key,
      name: ''
    });
    handleAddStep(addedStepKey, key);
  };

  const handleAddStep = (key, group) => {
    appendStep({
      key,
      group,
      description: '',
      images: []
    });
  };

  const handleRemoveGroupByKey = (key) => {
    const groupIndex = stepGroups.findIndex(group => group.key === key);
    if (groupIndex !== -1) {
      handleRemoveGroup(groupIndex);
    }
  };

  const handleRemoveGroup = (index) => {
    const removedGroupKey = stepGroups[index].key;
    removeGroup(index);

    const removeStepsKeys = steps.filter(step => step.group === removedGroupKey).map((step) => step.key);
    handleRemoveStepByKey(removeStepsKeys);
  };

  const handleRemoveStep = (index) => {
    removeStep(index);
  };

  const handleRemoveStepByKey = (key) => {
    if (Array.isArray(key)) {
      const indexesToRemove = [];
      key.forEach((stepKeyToRemove) => {
        const stepIndex = steps.findIndex(step => step.key === stepKeyToRemove);
        if (stepIndex !== -1) {
          indexesToRemove.unshift(stepIndex);
        }
      });

      if (indexesToRemove.length > 0) {
        handleRemoveStep(indexesToRemove);
      }
    } else {
      const stepIndex = steps.findIndex(step => step.key === key);
      if (stepIndex !== -1) {
        handleRemoveStep(stepIndex);
      }
    }
  };

  const handleDrop = useCallback(
    async (stepIndex, acceptedFiles) => {
      const images = stepsWatched[stepIndex].images || [];
      const preparedAcceptedFiles = [];

      // eslint-disable-next-line no-restricted-syntax
      for await (const file of acceptedFiles) {
        const {signedUrl, getImageUrl} = await getSignedUrl(file);
        await putFile(signedUrl, file);
        const imageData = {
          filename: file.name,
          preview: getImageUrl,
          url: getImageUrl
        };

        preparedAcceptedFiles.unshift(
          imageData
        );
      }

      setValue(`steps[${stepIndex}].images`, [
        ...images,
        ...preparedAcceptedFiles
      ]);
    },
    [setValue, stepsWatched]
  );


  const handleRemoveAll = useCallback((stepIndex) => {
      setValue(`steps[${stepIndex}].images`, []);
    },
    [setValue]
  );

  const handleRemove = useCallback((stepIndex, file) => {
      const filteredItems = stepsWatched[stepIndex].images && stepsWatched[stepIndex].images?.filter((_file) => _file.url !== file.url);

      setValue(`steps[${stepIndex}].images`, filteredItems);
    },
    [setValue, stepsWatched]
  );

  const getSteps = (group, groupIndex) => {
    let stepCounter = 1;

    return steps.map((step, stepIndex) =>
      (step.group === group.key) ? (
        <Stack spacing={2} key={step.key}>
          <Stack direction="row" spacing={2}>
            <Typography variant="h6" sx={{color: 'text.disabled'}}>
              {/* eslint-disable-next-line no-plusplus */}
              {`${t('page.recipe.add.form.preparation.step')} ${stepCounter++}`}
            </Typography>
            <Button
              size="small"
              color="error"
              startIcon={<Iconify icon="eva:trash-2-outline"/>}
              onClick={() => handleRemoveStepByKey(step.key)}
            >
              {t('page.recipe.add.form.preparation.remove')}
            </Button>
          </Stack>
          <LabelStyle sx={{mb: -1}}>{t('page.recipe.add.form.preparation.step_description')}</LabelStyle>
          <Controller
            name={`steps[${stepIndex}].description`}
            control={control}
            render={({field, fieldState}) => (
              <FofooEditor
                id={`preparation-step-description-${step.key}`}
                simple
                required
                placeholder={t('page.recipe.add.form.preparation.description')}
                name={`steps[${stepIndex}].description`}
                value={field.value}
                label={t('page.recipe.add.form.preparation.description')}
                onChange={field.onChange}
                error={!!fieldState.error}
                helperText={fieldState.error ? fieldState.error?.message : ''}
              />
            )}
          />

          <div>
            <LabelStyle>{t('page.recipe.add.form.preparation.images')}</LabelStyle>
            <RHFUpload
              multiple
              thumbnail
              showPreview
              name={`steps[${stepIndex}].images`}
              maxSize={1024 * 1024 * 16}
              onDrop={(acceptedFiles) => handleDrop(stepIndex, acceptedFiles)}
              onRemove={(file) => handleRemove(stepIndex, file)}
              onRemoveAll={() => handleRemoveAll(stepIndex)}
              showUploadButton={false}
            />
          </div>
        </Stack>
      ) : '');
  };

  return (
    <Container disableGutters>
      <Typography variant="h5" sx={{mb: 2}}>{t('page.recipe.add.form.preparation.title')}</Typography>
      <Stack divider={<Divider flexItem sx={{borderStyle: 'dashed'}}/>} spacing={3}>
        {stepGroups.map((group, index) => (
          <Card sx={{p: 3}} key={group.key}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Stack spacing={3} direction="row" sx={{mb: 3}}>
                  <Typography variant="h6" sx={{color: 'text.disabled'}}>
                    {`${t('page.recipe.add.form.preparation.group')} ${index + 1}`}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Iconify icon="eva:trash-2-outline"/>}
                    onClick={() => handleRemoveGroupByKey(group.key)}
                  >
                    Usuń
                  </Button>
                </Stack>
                <RHFTextField
                  size="small"
                  name={`stepGroups[${index}].name`}
                  label={t('page.recipe.add.form.preparation.group_name')}
                  InputLabelProps={{shrink: true}}
                />
                <Typography variant="body2" sx={{mt: 1}}>
                  {t('page.recipe.add.form.preparation.group_description')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography variant="h6" sx={{color: 'text.disabled', mb: 3}}>
                  {t('page.recipe.add.form.preparation.preparation_header')}
                </Typography>
                <Stack spacing={3} divider={<Divider flexItem sx={{borderStyle: 'dashed'}}/>}>
                  {getSteps(group, index)}
                </Stack>
                <Button size="small" startIcon={<Iconify icon="eva:plus-fill"/>}
                        onClick={() => handleAddStep(uuidv4(), group.key)}
                        sx={{flexShrink: 0, mt: 2}}>
                  {t('page.recipe.add.form.preparation.add_new_step')}
                </Button>
              </Grid>
            </Grid>
          </Card>
        ))}
      </Stack>
      <Box sx={{display: 'flex', justifyContent: 'space-between', flexDirection: {xs: 'column', sm: 'row'}}}>
        <Button size="small" startIcon={<Iconify icon="eva:plus-fill"/>}
                onClick={() => handleAddGroup(uuidv4())}
                sx={{flexShrink: 0, mt: 2}}>
          {t('page.recipe.add.form.preparation.add_new_group')}
        </Button>
        <Box sx={{width: {xs: '100%', sm: 'auto'}}}>
          <LoadingButton type="submit" variant="contained" size="large" loading={isSubmitting}
                         sx={{mt: 2, textAlign: 'right', width: {xs: '100%', sm: 'auto'}}}>
            {t('page.recipe.add.form.preparation.next_step')}
          </LoadingButton>
        </Box>
      </Box>
    </Container>
  );
}
